import type { PropsWithChildren } from 'react';
import * as Headless from '@headlessui/react';
import { ChevronDown } from 'lucide-react';

import { cls } from '@/utils/cls';

export type DisclosureProps = Omit<Headless.DisclosureProps<'div'>, 'as'> & {
  className?: string;
};

export function Disclosure({ children, className, ...rest }: DisclosureProps) {
  return (
    <div className={className}>
      <Headless.Disclosure defaultOpen={true} {...rest}>
        {children}
      </Headless.Disclosure>
    </div>
  );
}

export type DisclosureTitleProps = {
  onClick?: () => void; // Optional click handler
  className?: string; // Optional additional class name
};

export function DisclosureTitle({
  children,
  onClick, // Optional click handler
  className, // Optional additional class name
}: PropsWithChildren<DisclosureTitleProps>) {
  return (
    <Headless.Disclosure.Button
      onClick={onClick}
      className={cls(
        className,
        'flex w-full cursor-pointer items-center justify-between border-b border-gray-900/10 px-4 py-3 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75 ui-open:border-0',
      )}
    >
      {/* Title Text */}
      <div className="text-lg font-medium text-gray-900">{children}</div>

      {/* Expand/Collapse Icon */}
      <div className="flex items-center justify-center rounded-full p-1 hover:bg-gray-900/5">
        <ChevronDown
          className={cls(
            'h-6 w-6 text-gray-900/80 transition-transform duration-300 ui-open:rotate-180',
          )}
        />
      </div>
    </Headless.Disclosure.Button>
  );
}

export type DisclosurePanelProps = { className?: string };

export function DisclosurePanel({
  children,
  className,
}: PropsWithChildren<DisclosurePanelProps>) {
  return (
    <Headless.Disclosure.Panel className={className}>
      {children}
    </Headless.Disclosure.Panel>
  );
}
